import { useState, useEffect } from "react";
import { investInStrategy } from "../services/strategyService";
import { listBrokerageAccounts, type BrokerageAccount } from "../services/brokerageService";

interface InvestModalProps {
  isOpen: boolean;
  onClose: () => void;
  strategyName: string;
  strategyId?: string;
  onInvest?: (amount: number, brokerageAccountId: string) => Promise<void>;
  onSuccess?: () => void;
}

export function InvestModal({
  isOpen,
  onClose,
  strategyName,
  strategyId,
  onInvest,
  onSuccess,
}: InvestModalProps) {
  const [amount, setAmount] = useState(10000);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Brokerage accounts
  const [accounts, setAccounts] = useState<BrokerageAccount[]>([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState<string>("");

  // Load brokerage accounts when modal opens
  useEffect(() => {
    if (isOpen) {
      loadAccounts();
    }
  }, [isOpen]);

  const loadAccounts = async () => {
    setAccountsLoading(true);
    try {
      const response = await listBrokerageAccounts();
      const activeAccounts = (response.accounts || []).filter(a => a.status === "active");
      setAccounts(activeAccounts);
      
      // Auto-select default account
      const defaultAccount = activeAccounts.find(a => a.is_default);
      if (defaultAccount) {
        setSelectedAccountId(defaultAccount.id);
      } else if (activeAccounts.length > 0) {
        setSelectedAccountId(activeAccounts[0].id);
      }
    } catch (err) {
      console.error("Failed to load brokerage accounts:", err);
    } finally {
      setAccountsLoading(false);
    }
  };

  if (!isOpen) return null;

  const handleInvest = async () => {
    if (amount < 100) {
      setError("Minimum investment is $100");
      return;
    }

    // Brokerage account is REQUIRED for investments
    if (!selectedAccountId) {
      if (accountsLoading) {
        setError("Please wait for brokerage accounts to load");
      } else if (accounts.length === 0) {
        setError("Please connect a brokerage account first");
      } else {
        setError("Please select a brokerage account");
      }
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      if (onInvest) {
        // Use callback if provided - brokerage account is required
        await onInvest(amount, selectedAccountId);
      } else if (strategyId) {
        // Otherwise, invest directly using strategyId - brokerage account is required
        await investInStrategy(strategyId, amount, selectedAccountId);
      } else {
        throw new Error("No investment handler or strategy ID provided");
      }
      onClose();
      onSuccess?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to invest");
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const presetAmounts = [1000, 5000, 10000, 25000, 50000, 100000];

  const selectedAccount = accounts.find(a => a.id === selectedAccountId);

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div className="glass-card p-6 max-w-md w-full animate-slide-up">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-surface-100">
            Invest in Strategy
          </h2>
          <button
            onClick={onClose}
            className="text-surface-400 hover:text-surface-200 transition-colors"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="mb-6">
          <p className="text-surface-400 text-sm mb-4">
            You're about to invest in{" "}
            <span className="text-primary-400 font-medium">{strategyName}</span>
            . This will create a copy of the strategy in your portfolio.
          </p>

          {/* Brokerage Account Selection */}
          <label className="block text-surface-300 text-sm font-medium mb-2">
            Brokerage Account
          </label>
          {accountsLoading ? (
            <div className="flex items-center gap-2 text-surface-400 text-sm mb-4">
              <span className="spinner w-4 h-4" />
              Loading accounts...
            </div>
          ) : accounts.length === 0 ? (
            <div className="mb-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 text-yellow-400 text-sm">
              <p className="font-medium">No brokerage account connected</p>
              <p className="text-xs mt-1">
                Connect an Alpaca account in the <a href="/brokerage" className="underline hover:text-yellow-300">Brokerage</a> page to enable automated trading.
              </p>
            </div>
          ) : (
            <select
              value={selectedAccountId}
              onChange={(e) => setSelectedAccountId(e.target.value)}
              className="w-full px-3 py-2.5 bg-surface-800 border border-surface-600 rounded-lg text-surface-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors mb-4"
            >
              {accounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.account_name} ({account.account_type === "paper" ? "Paper" : "Live"})
                  {account.is_default ? " - Default" : ""}
                </option>
              ))}
            </select>
          )}

          {selectedAccount && (
            <div className="mb-4 p-3 rounded-lg bg-surface-800/50 border border-surface-700/50">
              <div className="flex items-center gap-2">
                <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                  selectedAccount.account_type === "paper" 
                    ? "bg-blue-500/20 text-blue-400" 
                    : "bg-purple-500/20 text-purple-400"
                }`}>
                  {selectedAccount.account_type === "paper" ? "Paper" : "Live"}
                </span>
                <span className="text-surface-300 text-sm font-medium">
                  {selectedAccount.account_name}
                </span>
                {selectedAccount.is_default && (
                  <span className="px-2 py-0.5 text-xs font-medium bg-primary-500/20 text-primary-400 rounded">
                    Default
                  </span>
                )}
              </div>
            </div>
          )}

          <label className="block text-surface-300 text-sm font-medium mb-2">
            Investment Amount
          </label>

          {/* Preset buttons */}
          <div className="flex flex-wrap gap-2 mb-3">
            {presetAmounts.map((preset) => (
              <button
                key={preset}
                onClick={() => setAmount(preset)}
                className={`px-3 py-1.5 text-sm rounded-lg transition-all duration-200 ${
                  amount === preset
                    ? "bg-primary-500 text-white"
                    : "bg-surface-700 text-surface-300 hover:bg-surface-600"
                }`}
              >
                ${preset.toLocaleString()}
              </button>
            ))}
          </div>

          {/* Custom amount input */}
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-surface-400 font-medium">
              $
            </span>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(Number(e.target.value) || 0)}
              min={100}
              step={100}
              className="input-field w-full pl-8 text-lg font-semibold"
              placeholder="Enter amount"
            />
          </div>
          <p className="text-surface-500 text-xs mt-2">
            Minimum investment: $100
          </p>
        </div>

        {error && (
          <div className="mb-4 bg-red-500/10 border border-red-500/50 rounded-lg p-3 text-red-400 text-sm">
            {error}
          </div>
        )}

        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 btn-secondary py-2.5"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            onClick={handleInvest}
            disabled={isLoading || amount < 100}
            className="flex-1 btn-primary py-2.5 flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <>
                <span className="spinner" />
                Investing...
              </>
            ) : (
              <>
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                Invest ${amount.toLocaleString()}
              </>
            )}
          </button>
        </div>

        <p className="text-surface-500 text-xs text-center mt-4">
          {selectedAccount?.account_type === "live" 
            ? "⚠️ Live trading - Real money will be used!"
            : "Paper trading only. No real money will be used."
          }
        </p>
      </div>
    </div>
  );
}
